import React, { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { GetCampaignFiltersApiResponse } from '@api/apiCompat';
import { CampaignFiltersDto,CreateCampaignDto, useCreateCampaignMutation } from '@api/apiCompat';
import { ERROR } from '@constants/auth';
import { API_ERROR_MSG_PATH, CAMPAIGN_ID, TOKENS } from '@constants/common';
import { ROUTING } from '@constants/routing';
import { useBalance } from '@hooks/api/useBalance';
import { useCampaignFiltersWithFallback } from '@hooks/api/useCampaignFiltersWithFallback';
import { useCampaignTypes } from '@hooks/api/useCampaignTypes';
import { useGetSearchParam } from '@hooks/common/useGetSearchParams';
import { useLeadScraperContext } from '@pages/LeadScraper/context';
import { ScraperData } from '@pages/LeadScraper/types';
import { isFormFilled, provideSchedulingData } from '@pages/LeadScraper/utils';
import { calcPaidFiltersPrice, calculateTotalPrice } from '@pages/LeadScraper/utils/calculatePrice';
import { getErrorMessage } from '@utils/getMessage';
import { useSnackbar } from 'notistack';

import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Divider, Typography } from '@mui/material';

import BasicInformationSummary from './components/BasicInformationSummary';
import FiltersSummary from './components/FiltersSummary';
import SchedulingSummary from './components/SchedulingSummary';
import ScraperSettingsSummary from './components/ScraperSettingsSummary';
import { StyledBox, StyledBoxContainer, StyledConfirmBox } from './styled';
import { useScreenSizeContext } from '@/shared/context/ScreenSizeContext';

// Helper to check if filter has the correct structure
function isCampaignFiltersDto(filter: any): filter is CampaignFiltersDto {
  return filter && 'campaign_filters_id' in filter && 'key' in filter && 'type' in filter;
}

// Helper to normalize campaign filters
function normalizeCampaignFilters(
  filters:
    | GetCampaignFiltersApiResponse
    | Array<{
        filter_id: number;
        filter_name: string;
        filter_type: string;
        required: boolean;
        placeholder: string;
      }>,
): CampaignFiltersDto[] {
  if (!filters || !Array.isArray(filters)) {
    return [];
  }

  return filters.map((filter: any) => {
    // If it's already in the correct format, return it
    if (isCampaignFiltersDto(filter)) {
      return filter;
    }

    // Otherwise, transform the fallback format to CampaignFiltersDto
    return {
      campaign_filters_id: filter.filter_id,
      key: filter.filter_name,
      type: filter.filter_type || 'text',
      campaign_type_id_campaign_type: filter.campaign_type_id || 0, // or appropriate default
      price: 0, // or appropriate default
      required: filter.required,
      placeholder: filter.placeholder,
    } as CampaignFiltersDto;
  });
}

// Helper to get filter ID and key
function getFilterIdAndKey(filter: any): { id: number; key: string } {
  if (isCampaignFiltersDto(filter)) {
    return { id: filter.campaign_filters_id ?? 0, key: filter.key ?? '' };
  }
  return { id: filter.filter_id || 0, key: filter.filter_name || '' };
}

const ScraperSummary: FC = () => {
  const { t } = useTranslation();
  const {
    sourceSettingsForm,
    basicInfoForm,
    filtersForm,
    submitButtonLocale,
    isEdit,
    setMobileShowSettings,
    mobileShowSettings,
    schedulingForm,
  } = useLeadScraperContext();
  const { autocomplete }: { autocomplete: ScraperData[] } = sourceSettingsForm.watch();
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const campaignId = useGetSearchParam(CAMPAIGN_ID);
  const [createCampaignMutation, { isLoading: isLoadingCreateCampaign }] = useCreateCampaignMutation();

  const { data: rawFiltersData } = useCampaignFiltersWithFallback(campaignId as string);
  const filtersData = normalizeCampaignFilters(rawFiltersData);
  const { data: balanceData } = useBalance();
  const { data: campaignTypes } = useCampaignTypes();

  // Map fallback integer IDs to actual UUIDs based on campaign type names
  const campaignTypeMapping: Record<string, string> = {
    '1': 'ce2e65e7-5fb5-461a-8391-81c30b034e5d', // Scraping Only (was Local Search in fallback)
    '2': '9e417b5f-76bb-4918-8030-dda0bf94ab3e', // Email Campaign
    '3': '2876085f-44e4-4b5e-989d-600c9d20b78f', // Combined
    '4': '8096b777-b3d0-4324-9b81-e002ed09a859', // Scraping Test
    '5': '6225b506-df9f-4634-b66b-f0c553abd6c0', // Google Search
  };

  // Get the actual campaign type UUID - check mapping first for integer IDs, then API data
  const actualCampaignTypeId =
    campaignTypeMapping[campaignId as string] ||
    campaignTypes?.find(ct => ct.campaign_type_id?.toString() === campaignId)?.campaign_type_id ||
    campaignId;
  const { isLargerScreen } = useScreenSizeContext();

  const formData = filtersForm.watch();
  const isFiltersFormChanged = isFormFilled(formData?.filters?.paidFilters);

  const allPaidFiltersSelected = Object.keys(formData?.filters?.paidFilters ?? {});

  const totalNumberOfLeads = autocomplete?.reduce((acc, val) => acc + Number(val?.filters?.numberOfLeads ?? '0'), 0);

  const paidFiltersPrice = isFiltersFormChanged
    ? calcPaidFiltersPrice(filtersData, allPaidFiltersSelected) * (totalNumberOfLeads > 0 ? totalNumberOfLeads : 1)
    : 0;

  const totalPrice = Number(calculateTotalPrice(autocomplete, paidFiltersPrice));
  const pricePerLead = totalNumberOfLeads > 0 ? (totalPrice / totalNumberOfLeads).toFixed(2) : 0;

  // Check individual form validity with fallbacks for normalization edge cases
  const hasAutocomplete = autocomplete?.length > 0;
  const sourceValid = sourceSettingsForm.formState.isValid;
  const basicValid = basicInfoForm.formState.isValid;
  const filtersValid = filtersForm.formState.isValid;

  // Alternative validation check: if all required data is present, consider valid
  // This handles cases where React Hook Form's isValid state hasn't updated after normalization
  const hasRequiredData = !!(
    hasAutocomplete &&
    basicInfoForm.getValues().campaignName &&
    basicInfoForm.getValues().campaignDescription &&
    autocomplete.every(
      item => item.scraper_name && item.filters?.numberOfLeads && parseInt(item.filters.numberOfLeads, 10) > 0,
    )
  );

  // Use form validation OR fallback validation if forms seem complete but validation is stale
  const isValid =
    (hasAutocomplete && sourceValid && basicValid && filtersValid) ||
    (hasRequiredData && !sourceSettingsForm.formState.isSubmitting);

  const accountBallance = balanceData?.token || 0;

  const handleSubmitCampaignData = async () => {
    try {
      // Transform frontend data to match backend expectations
      const scraperData = autocomplete.map(v => {
        const { numberOfLeads, ...restFilters } = v.filters;
        return {
          scraper_id: v.scrapers_id,
          leads_count: +numberOfLeads,
          filters: Object.entries(restFilters)
            .filter(([, value]) => !!value)
            .map(([key, value]) => {
              const foundFilter = filtersData.find(f => getFilterIdAndKey(f).key.toLowerCase() === key.toLowerCase())!;
              const filterId = getFilterIdAndKey(foundFilter).id;
              return {
                id: filterId,
                name: key,
                value: value!,
              };
            }),
        };
      });

      const paidFiltersData = Object.entries(filtersForm?.getValues()?.filters?.paidFilters ?? {})
        .filter(([, value]) => !!value)
        .map(([key, value]) => {
          const foundFilter = filtersData.find(f => getFilterIdAndKey(f).key.toLowerCase() === key.toLowerCase())!;
          const filterId = getFilterIdAndKey(foundFilter).id;
          return {
            id: filterId,
            name: key,
            value: value!,
          };
        });

      // Create config object that backend expects
      const config = {
        scrapers: scraperData,
        paid_filters: paidFiltersData,
        scraper_type: 'google', // Default scraper type
        search_query: basicInfoForm.getValues().campaignName, // Use campaign name as search query
        ...provideSchedulingData(schedulingForm.getValues()),
      };

      // Create filters object that backend expects
      const filters = {
        ...Object.fromEntries(paidFiltersData.map(f => [f.name, f.value])),
      };

      const createCampaignPayload: CreateCampaignDto = {
        campaign_type: actualCampaignTypeId as string,
        status: 'active', // enabled by default
        name: basicInfoForm.getValues().campaignName,
        description: basicInfoForm.getValues().campaignDescription,
        config,
        filters,
        schedule_type: 'once', // Default schedule type
        schedule_config: provideSchedulingData(schedulingForm.getValues()) || undefined,
      };

      const messageText = isEdit ? 'common.campaignUpdated' : 'common.campaignCreated';

      if (!isEdit) {
        await (createCampaignMutation as any)({
          campaignRequest: createCampaignPayload,
        }).unwrap();
      }

      // Show token reservation feedback
      if (totalPrice > 0) {
        snackbar.enqueueSnackbar(`${t(messageText)} — ${totalPrice} tokens reserved`, { variant: 'success' });
      } else {
        snackbar.enqueueSnackbar(t(messageText));
      }

      // Small delay to allow balance refetch animation to trigger before navigation
      setTimeout(() => navigate(ROUTING.ROOT), 800);
    } catch (err) {
      snackbar.enqueueSnackbar(getErrorMessage(err, API_ERROR_MSG_PATH), { variant: ERROR });
    }
  };

  if (mobileShowSettings && !isLargerScreen) return null;

  return (
    <StyledBoxContainer
      maxWidth="550px"
      minWidth={isLargerScreen ? '444px' : '390px'}
      display="flex"
      flexDirection="column"
    >
      <Box display="flex" flexDirection="column" p={3} height="100px" justifyContent="center">
        <Typography variant="subtitle1">{t('dashboard.newScraper.summary.title')}</Typography>
        <Typography variant="body2">{t('dashboard.newScraper.summary.subtitle')}</Typography>
      </Box>
      <Divider />

      <StyledBox display="flex" flexDirection="column" gap={isLargerScreen ? 2 : 1} height="100%">
        <BasicInformationSummary />
        <ScraperSettingsSummary />
        <FiltersSummary
          filtersData={filtersData}
          isFormChanged={isFiltersFormChanged}
          selectedFilters={allPaidFiltersSelected}
          formData={formData}
          totalNumberOfLeads={totalNumberOfLeads}
        />
        <SchedulingSummary />
        {!isLargerScreen && (
          <Button variant="contained" color="secondary" size="large" onClick={() => setMobileShowSettings(true)}>
            EDIT Scraper
          </Button>
        )}
      </StyledBox>

      <StyledConfirmBox p={2} height="120px">
        <Box display="flex" justifyContent="space-between" p={1}>
          <Typography variant="caption">
            {pricePerLead} {TOKENS} / {t('dashboard.newScraper.leadLabel')}
          </Typography>
          <Typography variant="subtitle2" color={accountBallance < totalPrice ? 'red' : 'currentColor'}>
            {t('dashboard.newScraper.summary.total')}: {totalPrice} {TOKENS}
          </Typography>
        </Box>
        <LoadingButton
          variant="contained"
          fullWidth
          disabled={!isValid || accountBallance < totalPrice}
          onClick={handleSubmitCampaignData}
          loading={isLoadingCreateCampaign}
        >
          {t(submitButtonLocale)}
        </LoadingButton>

        <Typography variant="body2" mt={1} color="red" height="20px" aria-label="not-enough-tokens-validation-text">
          {accountBallance < totalPrice && t('billing.scraper.notEnoughTokens')}
        </Typography>
      </StyledConfirmBox>
    </StyledBoxContainer>
  );
};

export default memo(ScraperSummary);
